var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { faHdd } from "@fortawesome/free-solid-svg-icons";
import { computed, ref, set } from "vue";
import { GalaxyApi } from "@/api";
import { HistoriesFilters } from "@/components/History/HistoriesFilters";
import { SELECTION_STATES, } from "@/components/SelectionDialog/selectionTypes";
import { errorMessageAsString } from "@/utils/simple-error";
import SelectionDialog from "@/components/SelectionDialog/SelectionDialog.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryDatasetPicker',
    props: {
        folderId: null,
        title: { default: "Select datasets from your histories" },
        actionButtonText: { default: "Select" }
    },
    emits: ["reload", "onClose", "onSelect"],
    setup(__props, { emit }) {
        const props = __props;
        const selectionDialog = ref();
        const totalItems = ref(0);
        const loading = ref(false);
        const hasValue = ref(false);
        const modalShow = ref(true);
        const errorMessage = ref("");
        const submitting = ref(false);
        const allSelected = ref(false);
        const datasetsVisible = ref(false);
        const items = ref([]);
        const selected = ref([]);
        const itemsProvider = ref(historiesProvider);
        const searchTitle = computed(() => {
            if (datasetsVisible.value) {
                return "datasets";
            }
            else {
                return "search histories";
            }
        });
        const okButtonText = computed(() => {
            if (selected.value.length === 0) {
                return props.actionButtonText;
            }
            else {
                return `${props.actionButtonText} ${selected.value.length} dataset${selected.value.length > 1 ? "s" : ""}`;
            }
        });
        const fields = computed(() => {
            if (datasetsVisible.value) {
                return [
                    { key: "label", label: "Name", sortable: true },
                    { key: "update_time", label: "Last Updated", sortable: true },
                ];
            }
            else {
                return [
                    { key: "label", label: "History Name" },
                    { key: "size", label: "Datasets", sortable: false },
                    { key: "update_time", label: "Last Updated" },
                ];
            }
        });
        const selectAllIcon = computed(() => {
            if (allSelected.value) {
                return SELECTION_STATES.SELECTED;
            }
            else if (selected.value.length > 0) {
                return SELECTION_STATES.MIXED;
            }
            else {
                return SELECTION_STATES.UNSELECTED;
            }
        });
        function historyEntryToRecord(entry) {
            const result = {
                id: entry.id,
                label: entry.name,
                details: entry.annotation || "",
                isLeaf: false,
                url: entry.url,
                size: entry.count,
                update_time: entry.update_time,
            };
            return result;
        }
        function datasetEntryToRecord(entry) {
            const result = {
                id: entry.id,
                label: entry.name || "",
                details: "",
                isLeaf: true,
                url: entry.url,
                update_time: entry.update_time,
            };
            return result;
        }
        function formatRows() {
            hasValue.value = selected.value.length > 0;
            for (const item of items.value) {
                if (item.isLeaf) {
                    const _rowVariant = selected.value.findIndex((i) => i.id === item.id) !== -1
                        ? SELECTION_STATES.SELECTED
                        : SELECTION_STATES.UNSELECTED;
                    set(item, "_rowVariant", _rowVariant);
                }
            }
            allSelected.value = checkIfAllSelected();
        }
        function checkIfAllSelected() {
            return Boolean(items.value.length &&
                items.value.every((item) => {
                    return selected.value.findIndex((i) => i.id === item.id) !== -1;
                }));
        }
        function historiesProvider(ctx, url) {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                loading.value = true;
                try {
                    const filter = ctx.filter;
                    if (filter) {
                        (_a = selectionDialog.value) === null || _a === void 0 ? void 0 : _a.resetPagination();
                    }
                    const limit = ctx.perPage;
                    const offset = (ctx.currentPage - 1) * ctx.perPage;
                    const sortDesc = ctx.sortDesc;
                    const sortBy = ctx.sortBy === "label" ? "name" : ctx.sortBy || "update_time";
                    const queryDict = HistoriesFilters.getQueryDict(ctx.filter);
                    const { response, data, error } = yield GalaxyApi().GET("/api/histories", {
                        params: {
                            query: {
                                search: filter,
                                view: "summary",
                                keys: "create_time",
                                show_own: true,
                                offset: offset,
                                limit: limit,
                                q: Object.keys(queryDict),
                                qv: Object.values(queryDict),
                                sort_by: sortBy,
                                sort_desc: sortDesc,
                            },
                        },
                    });
                    if (error) {
                        errorMessage.value = errorMessageAsString(error);
                        return [];
                    }
                    totalItems.value = parseInt((_b = response.headers.get("total_matches")) !== null && _b !== void 0 ? _b : "0");
                    items.value = data.map(historyEntryToRecord);
                    formatRows();
                    return items.value;
                }
                catch (error) {
                    errorMessage.value = errorMessageAsString(error);
                    return [];
                }
                finally {
                    loading.value = false;
                }
            });
        }
        function datasetsProvider(ctx, selectedHistory) {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                loading.value = true;
                try {
                    const limit = ctx.perPage;
                    const offset = (ctx.currentPage - 1) * ctx.perPage;
                    const query = ctx.filter || "";
                    const querySortBy = ctx.sortBy === "time" ? "update_time" : "name";
                    const sortPrefix = ctx.sortDesc ? "-dsc" : "-asc";
                    const { response, data, error } = yield GalaxyApi().GET("/api/datasets", {
                        params: {
                            query: {
                                history_id: selectedHistory.id,
                                q: ["name-contains"],
                                qv: [query],
                                order: `${querySortBy}${sortPrefix}`,
                                offset: offset,
                                limit: limit,
                            },
                        },
                    });
                    if (error) {
                        errorMessage.value = errorMessageAsString(error);
                        return [];
                    }
                    totalItems.value = parseInt((_a = response.headers.get("total_matches")) !== null && _a !== void 0 ? _a : "0");
                    items.value = data.map(datasetEntryToRecord);
                    formatRows();
                    return items.value;
                }
                catch (error) {
                    errorMessage.value = errorMessageAsString(error);
                    return [];
                }
                finally {
                    loading.value = false;
                }
            });
        }
        function onHistoryClick(item) {
            var _a, _b;
            if (!item.isLeaf) {
                (_a = selectionDialog.value) === null || _a === void 0 ? void 0 : _a.resetFilter();
                (_b = selectionDialog.value) === null || _b === void 0 ? void 0 : _b.resetPagination();
                datasetsVisible.value = true;
                itemsProvider.value = (ctx) => datasetsProvider(ctx, item);
            }
        }
        function onDatasetClick(item) {
            return __awaiter(this, void 0, void 0, function* () {
                if (item.isLeaf) {
                    if (selected.value.findIndex((i) => i.id === item.id) !== -1) {
                        selected.value = selected.value.filter((i) => i.id !== item.id);
                    }
                    else {
                        selected.value.push(item);
                    }
                    formatRows();
                }
            });
        }
        function selectAll() {
            if (allSelected.value) {
                selected.value = [];
            }
            else {
                for (const item of items.value) {
                    if (selected.value.findIndex((i) => i.id === item.id) === -1) {
                        selected.value.push(item);
                    }
                }
            }
            formatRows();
        }
        function onUndo() {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                (_a = selectionDialog.value) === null || _a === void 0 ? void 0 : _a.resetFilter();
                (_b = selectionDialog.value) === null || _b === void 0 ? void 0 : _b.resetPagination();
                selected.value = [];
                datasetsVisible.value = false;
                itemsProvider.value = historiesProvider;
            });
        }
        function onOk() {
            emit("onSelect", selected.value);
            emit("onClose");
        }
        function onCancel() {
            modalShow.value = false;
            emit("onClose");
        }
        return { __sfc: true, props, emit, selectionDialog, totalItems, loading, hasValue, modalShow, errorMessage, submitting, allSelected, datasetsVisible, items, selected, itemsProvider, searchTitle, okButtonText, fields, selectAllIcon, historyEntryToRecord, datasetEntryToRecord, formatRows, checkIfAllSelected, historiesProvider, datasetsProvider, onHistoryClick, onDatasetClick, selectAll, onUndo, onOk, onCancel, faHdd, HistoriesFilters, SelectionDialog };
    }
});
